import type { Graphics } from 'pixi.js';
import { lerpColor } from '../../color';
import type { WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const CANALS = 8;
const FRILLS = 16;
const BUBBLES = 16;
const SHELLS = 4;
const RING_BUBBLES = 12;
const BELLS = 8;

function shell(g: Graphics, x: number, y: number, size: number, a: number): void {
  const hx = x - Math.cos(a) * size * 0.45;
  const hy = y - Math.sin(a) * size * 0.45;
  g.moveTo(hx, hy);
  for (let j = 0; j <= 10; j++) {
    const b = a - 0.85 + (1.7 * j) / 10;
    const r = size * (j % 2 === 0 ? 1 : 0.9);
    g.lineTo(hx + Math.cos(b) * r, hy + Math.sin(b) * r);
  }
  g.closePath();
}

function ribs(g: Graphics, x: number, y: number, size: number, a: number): void {
  const hx = x - Math.cos(a) * size * 0.45;
  const hy = y - Math.sin(a) * size * 0.45;
  for (const d of [-0.55, -0.18, 0.18, 0.55]) {
    g.moveTo(hx, hy).lineTo(hx + Math.cos(a + d) * size * 0.85, hy + Math.sin(a + d) * size * 0.85);
  }
}

function bell(g: Graphics, x: number, y: number, s: number): void {
  g.moveTo(x - s, y).arc(x, y, s, Math.PI, TAU);
  for (let j = 1; j <= 4; j++) g.lineTo(x + s - (j * s) / 2, y + (j % 2 === 1 ? s * 0.22 : 0));
  g.closePath();
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    const sx = body.scaleX;
    const sy = body.scaleY;
    g.ellipse(body.x - r * 0.08 * sx, body.y - r * 0.1 * sy, r * 0.6 * sx, r * 0.6 * sy).fill({
      color: lerpColor(color, pal.rim, 0.4),
      alpha: 0.3 * alpha,
    });
    for (let i = 0; i < CANALS; i++) {
      const a = (i / CANALS) * TAU + TAU / 16;
      g.moveTo(body.x + Math.cos(a) * r * 0.32 * sx, body.y + Math.sin(a) * r * 0.32 * sy).lineTo(
        body.x + Math.cos(a) * r * 0.9 * sx,
        body.y + Math.sin(a) * r * 0.9 * sy,
      );
    }
    g.stroke({ width: Math.max(1, r * 0.045), color: lerpColor(color, pal.ink, 0.35), alpha: 0.32 * alpha, cap: 'round' });
    for (let j = 0; j <= FRILLS * 4; j++) {
      const a = (j / (FRILLS * 4)) * TAU;
      const rr = r * (0.8 + 0.035 * Math.cos(FRILLS * a));
      const x = body.x + Math.cos(a) * rr * sx;
      const y = body.y + Math.sin(a) * rr * sy;
      if (j === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke({ width: Math.max(1, r * 0.04), color: lerpColor(color, pal.ink, 0.3), alpha: 0.42 * alpha });
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU + TAU / 8;
      const gx = body.x + Math.cos(a) * r * 0.2 * sx;
      const gy = body.y + Math.sin(a) * r * 0.2 * sy;
      g.ellipse(gx, gy, r * 0.12 * sx, r * 0.12 * sy);
    }
    g.stroke({ width: Math.max(1, r * 0.05), color: lerpColor(color, pal.held, 0.55), alpha: 0.55 * alpha });
    k.rimLight(body, 0.5 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    const sx = body.scaleX;
    const sy = body.scaleY;
    k.trace(body);
    g.fill({ color: lerpColor(fill, pal.heldShade, 0.55), alpha });
    g.ellipse(body.x - r * 0.07 * sx, body.y - r * 0.09 * sy, r * 0.87 * sx, r * 0.87 * sy).fill({ color: fill, alpha });
    g.ellipse(body.x - r * 0.2 * sx, body.y - r * 0.24 * sy, r * 0.48 * sx, r * 0.48 * sy).fill({ color: pal.heldLight, alpha: 0.5 * alpha });
    k.arcLine(body, r * 0.8, 0.2, 1.5);
    g.stroke({ width: Math.max(1, r * 0.07), color: pal.spot, alpha: 0.38 * alpha, cap: 'round' });
    k.arcLine(body, r * 0.64, -2.8, -1.8);
    g.stroke({ width: Math.max(1.2, r * 0.1), color: pal.rim, alpha: 0.85 * alpha, cap: 'round' });
    g.circle(body.x + Math.cos(-1.45) * r * 0.64 * sx, body.y + Math.sin(-1.45) * r * 0.64 * sy, Math.max(1, r * 0.05)).fill({
      color: pal.rim,
      alpha: 0.85 * alpha,
    });
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, pal.ink, 0.5), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.07 * alpha });
    const s = Math.max(2, k.unit * 0.011);
    const size = (i: number): number => (i % 2 === 0 ? s : s * 0.7);
    k.ringDots(body, reach, BUBBLES, spin, (x, y, i) => {
      g.circle(x, y, size(i));
    });
    g.stroke({ width: Math.max(1, s * 0.4), color: pal.spot, alpha: 0.6 * alpha });
    k.ringDots(body, reach, BUBBLES, spin, (x, y, i) => {
      g.circle(x - size(i) * 0.35, y - size(i) * 0.35, size(i) * 0.3);
    });
    g.fill({ color: pal.rim, alpha: 0.7 * alpha });
    const sh = Math.max(4, k.unit * 0.024);
    k.ringDots(body, reach + sh * 0.9, SHELLS, spin + TAU / 32, (x, y, _i, a) => {
      shell(g, x, y, sh, a);
    });
    g.fill({ color: pal.rim, alpha: 0.75 * alpha });
    k.ringDots(body, reach + sh * 0.9, SHELLS, spin + TAU / 32, (x, y, _i, a) => {
      ribs(g, x, y, sh, a);
    });
    g.stroke({ width: 1, color: pal.ink, alpha: 0.55 * alpha, cap: 'round' });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const r = radius * 1.2;
    g.circle(x, y, r);
    if (on) g.fill({ color: last ? pal.spot : pal.incoming.circle, alpha: 0.85 * alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.5) * alpha });
    g.circle(x - r * 0.35, y - r * 0.38, r * 0.26).fill({ color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(7, k.unit * 0.04);
    const wave = k.unit * 0.006;
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const len = Math.hypot(dx, dy);
      if (len <= 0) continue;
      const nx = -dy / len;
      const ny = dx / len;
      const n = Math.min(80, Math.floor(len / step));
      for (let s = 1; s < n; s++) {
        const t = s / n;
        const off = Math.sin(s * 0.9) * wave;
        g.circle(x0 + dx * t + nx * off, y0 + dy * t + ny * off, s % 3 === 0 ? 2 : 1.2);
      }
    }
    g.fill({ color: pal.path, alpha: alpha * 2.4 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) {
      g.star(x, y, 4, size * 1.2, size * 0.35, rot);
      g.fill({ color, alpha });
      return;
    }
    g.circle(x, y, size).stroke({ width: Math.max(1, size * 0.35), color, alpha });
    g.circle(x - size * 0.35, y - size * 0.35, size * 0.3).fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const rise = m.grow * m.unit * 0.1;
    for (let i = 0; i < RING_BUBBLES; i++) {
      const a = (i / RING_BUBBLES) * TAU + 0.2;
      const d = m.r * (1 + 0.06 * Math.sin(i * 2.3));
      g.circle(m.x + Math.cos(a) * d, m.y + Math.sin(a) * d - rise * 0.5, m.width * (i % 2 === 0 ? 1.3 : 0.9));
    }
    g.stroke({ width: m.width * 0.45, color: pal.rim, alpha: m.fade * 0.5 });
    g.circle(m.x, m.y, m.r * 1.22).stroke({ width: m.width * 1.6, color: pal.spot, alpha: m.fade * 0.16 });
    g.circle(m.x, m.y, m.r * 1.22).stroke({ width: m.width * 0.6, color: pal.spot, alpha: m.fade * 0.42 });
    const s = Math.max(3, m.unit * 0.022);
    for (let i = 0; i < BELLS; i++) {
      const a = (i / BELLS) * TAU + TAU / 16;
      const d = m.r * 1.45;
      const bx = m.x + Math.cos(a) * d;
      const by = m.y + Math.sin(a) * d - rise * 1.6;
      bell(g, bx, by, s);
      g.fill({ color: pal.incoming.circle, alpha: m.fade * 0.42 });
      for (const o of [-0.5, 0, 0.5]) {
        g.moveTo(bx + o * s, by + s * 0.15).quadraticCurveTo(bx + o * s + s * 0.35, by + s * 0.9, bx + o * s, by + s * 1.6);
      }
      g.stroke({ width: Math.max(1, s * 0.16), color: pal.incoming.circle, alpha: m.fade * 0.32, cap: 'round' });
    }
  },

  trail(g, x, y, size, angle) {
    g.circle(x, y, size);
    g.circle(x + Math.cos(angle) * size * 0.6, y - size * 2.4, size * 0.5);
  },
};
